import { Col, DatePicker, Form, Input, InputNumber, message, Modal, Row, Select, Switch, Upload } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { getallProject, getallProjectForReceipts, getAllReceipts, getCustomers, getInvoiceByID, getReceiptsByID, updateInvoice, updateReceipts } from "../../utils/API";
import { Currency } from "lucide-react";

interface EditInvoiceProps {
    visible: boolean;
    id: string;
    onClose?: () => void;
    onCancel?: () => void;
    onTaskCreated?: () => void;
    onSuccess?: () => void;
    // record?: ReceiptsFormValues | null;
}

interface InvoiceFormValues {
    _id: string;
    [key: string]: any;
    invoice_number: string;
    project: string;
    customer_name: string;
    amount: number;
    currency: string;
    invoice_date: string;
    due_date: string;
    description: string;
}

const EditInvoiceModel: React.FC<EditInvoiceProps> = (props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();
    const [invoice, setInvoice] = useState<any>(null);
    const [customers, setCustomers] = useState<any>([]);
    const [invoiceParts, setInvoiceParts] = useState({
        prefix: "",
        year: "",
        seq: "",
    });
    const [projects, setProjects] = useState<any>([]);

    const onFinish = async (values: InvoiceFormValues) => {
        setIsLoading(true);
        try {
            const formData = new FormData();

            const relatedProject = projects.find(
                (p: any) => p._id === values.project
            );

            if (values.invoice_date) {
                values.invoice_date = dayjs(values.invoice_date).format('DD-MM-YYYY'); // Change format as needed
            }

            if (values.due_date) {
                values.due_date = dayjs(values.due_date).format('DD-MM-YYYY'); // Change format as needed
            }

            const finalValues = {
                ...values,
                project: relatedProject ? values.project : null,
            };

            if (props.id) {
                await updateInvoice(props.id, finalValues);
                message.success("Invoice updated successfully!");
            }
            // getAllReceipts("");
            props.onSuccess?.();
        } catch (error: any) {
            message.error(error.response?.data?.message || "Failed to save invoice");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (invoice) {
            const parts = parseInvoiceNumber(invoice.invoice_number);
            setInvoiceParts(parts);

            form.setFieldsValue({
                customer_name: invoice.customer_name,
                project: invoice.project || undefined,
                // invoice_number: invoice.invoice_number,
                amount: invoice.amount,
                currency: invoice.currency,
                invoice_date: invoice.invoice_date ? dayjs(invoice.invoice_date, "DD-MM-YYYY") : null,
                due_date: invoice.due_date ? dayjs(invoice.due_date, "DD-MM-YYYY") : null,
                description: invoice.description,
            });
        }
    }, [invoice, form]);

    const fetchInvoice = async () => {
        setIsLoading(true);
        try {
            if (props.id) {
                const data = await getInvoiceByID(props.id);
                setInvoice(data);
            }
        } catch (error) {
            message.error("Failed to fetch invoice data.");
        } finally {
            setIsLoading(false);
        }
    }

    const fetchProjects = async () => {
        setIsLoading(true);
        try {
            const response: any = await getallProjectForReceipts("", "", true);

            if (response?.data) {
                const activeProjects = response.data.filter(
                    (client: any) => client.isActive === true
                );
                setProjects(activeProjects);
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const parseInvoiceNumber: any = (invoice: string) => {
        if (!invoice) return { prefix: "", year: "", seq: "" };

        const parts = invoice.split("-");
        return {
            prefix: parts[0] || "",
            year: parts[1] || "",
            seq: parts[2] || "",
        };
    };

    useEffect(() => {
        fetchInvoice();
        fetchProjects();
    }, [props.id]);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const response: any = await getCustomers("", "");

                if (response?.data) {
                    // Filter only active clients
                    const activeCustomers = response.data.filter(
                        (client: any) => client.isActive === true
                    );
                    setCustomers(activeCustomers);
                }
            } catch (error) {
                console.error("Error fetching customers:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCustomers();
    }, []);

    // Watch the currently selected project
    const selectedProjectId = Form.useWatch("project", form);

    // Find the project object
    const selectedProject = projects.find((p: any) => p._id === selectedProjectId);

    // Filter customers: only show the customer linked to selected project
    const filteredCustomers = selectedProject
        ? customers.filter((c: any) => c._id === selectedProject.clientId?._id)
        : [];

    return (
        <Modal
            visible={props.visible}
            onCancel={props.onClose}
            footer={null}
            width={800}
            centered={true}
            destroyOnClose
            className="custom-modal"
            bodyStyle={{
                overflowY: "auto",
                fontFamily: "inter, sans-serif",
            }}
            style={{
                bottom: 20,
                top: 20,
                // maxHeight: 'calc(100vh - 30px)',
                margin: '0 auto',
                position: 'relative',
            }}
            styles={{
                header: {
                    padding: "12px",
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#f1f5ffff",
                },
                content: {
                    padding: 0,
                    overflow: "hidden",
                },
            }}
            title={
                <div
                    style={{
                        color: "#4b5563",
                        fontFamily: "inter, sans-serif",
                        fontSize: "20px",
                        fontWeight: 500,
                    }}
                >
                    Edit Invoice
                </div>
            }
        >
            <Form onFinish={onFinish} form={form} layout="horizontal"
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 19 }}
                colon={false} style={{ margin: 0 }}>
                <div className="elements">
                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Project
                            </span>
                        }
                        name="project"
                        required={false}
                    >
                        <Select
                            size="large"
                            allowClear
                            // disabled={roleId === 2}
                            showSearch
                            optionFilterProp="children"
                            loading={isLoading}
                            placeholder="Select Project"
                            className="CustomSelect"
                            onChange={(projectId) => {
                                const selectedProject = projects.find(
                                    (p: any) => p._id === projectId
                                );

                                if (selectedProject?.clientId?._id) {
                                    form.setFieldsValue({
                                        customer_name: selectedProject.clientId?._id
                                    });
                                }
                            }}
                        >
                            {projects.map((project: any) => (
                                <Select.Option key={project._id} value={project._id}>
                                    {project.projectName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Customer Name
                            </span>
                        }
                        name="customer_name"
                        required={false}
                    >
                        <Select
                            size="large"
                            // disabled={roleId === 2}
                            showSearch
                            optionFilterProp="children"
                            loading={isLoading}
                            placeholder="Select Customer Name"
                            className="CustomSelect"
                            onChange={(customerId) => {
                                const relatedProject = projects.find(
                                    (p: any) => p.clientId?._id === customerId
                                );

                                if (relatedProject) {
                                    form.setFieldsValue({
                                        project: relatedProject._id,
                                    });
                                } else {
                                    form.resetFields(["project"]);
                                }
                            }}
                        >
                            {customers.map((customer: any) => (
                                <Select.Option key={customer._id} value={customer._id}>
                                    {customer.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Invoice Date
                            </span>
                        }
                        name="invoice_date"
                        required={false}
                    >
                        <DatePicker className="Allinput" format="DD-MM-YYYY" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Due Date
                            </span>
                        }
                        name="due_date"
                        required={false}
                    >
                        <DatePicker className="Allinput" format="DD-MM-YYYY" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="currency"
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Currency
                            </span>
                        }
                        required={false}
                    >
                        <Select
                            placeholder="Select Currency"
                            className="CustomSelect"
                            showSearch
                            optionFilterProp="children"
                            // value={position}
                            onChange={(value) => {
                                // setPosition(value);
                                form.setFieldsValue({ technology: undefined });
                            }}
                        >
                            <Select.Option value="₹ INR">₹ INR</Select.Option>
                            <Select.Option value="$ USD">$ USD</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Amount
                            </span>
                        }
                        name="amount"
                        required={false}
                    >
                        <Input type="Number" placeholder="Amount" className="Allinput" />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Description <span className="text-red-500">*</span>
                            </span>
                        }
                        name="description"
                        required={false}
                        rules={[{ required: true, message: "Please enter description" }]}
                    >
                        <Input.TextArea
                            placeholder="Enter description"
                            className="Allinputtextarea"
                        />
                    </Form.Item>
                </div>
                <Row justify="end">
                    <Col
                        xs={24}
                        sm={24}
                        md={24}
                        xl={24}
                        xxl={20}
                        className="d-flex justify-content-end me-4"
                    >
                        <div className="button-wrapper">
                            <button
                                className="allcanclebutton"
                                onClick={props.onClose}
                                type="button"
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="allsubmitebutton w-20"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Updating...' : 'Update'}
                            </button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default EditInvoiceModel;